import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, m } from "framer-motion";
import { X } from "lucide-react";
import { createContext, useContext, useState, type ReactNode } from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";

/**
 * The Sheet's own panel node, published so that portaled popup primitives
 * (Combobox, Select, DatePicker, …) opened *inside* a Sheet can render their
 * floating content into it instead of `document.body`. This keeps that
 * content within the Sheet's Radix Dialog `react-remove-scroll` boundary —
 * without it, the scroll-lock blocks wheel/touch events on the portaled
 * listbox (which lives outside the boundary), so an overflowing Combobox
 * dropdown can't be scrolled by mouse wheel even though it has
 * `overflow-y-auto`. `null` when not inside a Sheet (the primitives then fall
 * back to their default body portal).
 */
const SheetPortalContext = createContext<HTMLElement | null>(null);

/**
 * Container into which popup primitives inside a Sheet should portal their
 * floating content. Returns the Sheet's panel node, or `null` when not inside
 * a Sheet.
 */
export function useSheetPortalContainer(): HTMLElement | null {
  return useContext(SheetPortalContext);
}

export interface SheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string;
  /**
   * Optional glyph shown in a tinted badge left of the title. It turns the
   * header into the compact variant: the title and description shrink one step
   * so the badge, a short label and its one-line explainer read as a single
   * band rather than a page heading. Use it when the panel's own title is
   * generic ("Taakdetails") and the subject is stated inside the body.
   */
  readonly icon?: ReactNode;
  /** Which edge the panel docks to. */
  readonly side?: "right" | "bottom";
  /**
   * Width of a right-docked panel. Defaults to `md` (30rem). `lg` (36rem)
   * and `xl` (42rem) give roomier forms more comfortable two-column layouts,
   * closer to the ledger concept's dossier surface. Ignored for the bottom
   * variant, which is always full width.
   */
  readonly size?: "md" | "lg" | "xl";
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
}

const rightWidths = {
  md: "w-[min(30rem,100vw)]",
  lg: "w-[min(36rem,100vw)]",
  xl: "w-[min(42rem,100vw)]",
} as const;

const panelMotion = {
  right: {
    initial: { x: "100%" },
    animate: { x: 0 },
    exit: { x: "100%" },
    className: "fixed right-0 top-0 h-dvh rounded-l-3xl border-l",
  },
  bottom: {
    initial: { y: "100%" },
    animate: { y: 0 },
    exit: { y: "100%" },
    className:
      "fixed inset-x-0 bottom-0 max-h-[88dvh] w-full rounded-t-3xl border-t",
  },
} as const;

/**
 * Accessible slide-over panel (Radix Dialog under the hood for focus trapping,
 * escape-to-close and scroll lock) with a framer slide. Docks to the right by
 * default; the bottom variant works as a mobile-style sheet.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  icon,
  side = "right",
  size = "md",
  children,
  footer,
  className,
}: SheetProps) {
  const motion = panelMotion[side];
  const overlayTransition = useMotionTransition({ duration: 0.2 });
  const panelTransition = useMotionTransition(springSoft);
  // Captured after mount so popup primitives rendered inside `children` can
  // portal their floating content into this panel (see SheetPortalContext).
  const [panel, setPanel] = useState<HTMLElement | null>(null);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <m.div
                className="fixed inset-0 z-50 bg-foreground/35 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={overlayTransition}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              forceMount
              // Radix links the panel to its Description through an id it
              // generates upfront, and warns when that id resolves to nothing.
              // A Sheet without a description is deliberate (the title carries
              // the whole meaning), so the link is dropped for that case only --
              // spreading it unconditionally would also unlink the descriptions
              // that do exist.
              {...(description ? {} : { "aria-describedby": undefined })}
            >
              <m.div
                ref={setPanel}
                initial={motion.initial}
                animate={motion.animate}
                exit={motion.exit}
                transition={panelTransition}
                className={cn(
                  // `overflow-hidden` clips the header/footer bands to the
                  // panel's rounded corners. Without it the footer's tinted
                  // background paints square into the bottom corner, showing
                  // as a grey notch outside the curve — and it stays correct
                  // for both the right (rounded-l) and bottom (rounded-t)
                  // variants, unlike hardcoding a corner on the footer.
                  "z-50 flex flex-col overflow-hidden border-border bg-card text-card-foreground shadow-lg focus:outline-none",
                  motion.className,
                  side === "right" && rightWidths[size],
                  className,
                )}
              >
                <div className="flex items-start justify-between gap-4 border-b border-border/60 px-6 py-4">
                  <div className="flex min-w-0 items-center gap-3">
                    {icon && (
                      <span
                        aria-hidden="true"
                        className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary [&_svg]:size-5"
                      >
                        {icon}
                      </span>
                    )}
                    <div className="min-w-0 space-y-1">
                      <DialogPrimitive.Title
                        className={cn(
                          "font-semibold tracking-tight",
                          icon ? "text-sm" : "text-lg",
                        )}
                      >
                        {title}
                      </DialogPrimitive.Title>
                      {description && (
                        <DialogPrimitive.Description
                          className={cn(
                            "text-muted-foreground",
                            icon ? "truncate text-xs" : "text-sm",
                          )}
                        >
                          {description}
                        </DialogPrimitive.Description>
                      )}
                    </div>
                  </div>
                  <DialogPrimitive.Close
                    className="grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Sluiten"
                  >
                    <X className="size-4" />
                  </DialogPrimitive.Close>
                </div>
                {children && (
                  <div
                    // A scrollable region is only a tab stop when it has no
                    // focusable children — a keyboard user must otherwise be
                    // able to reach the overflow. Sheet content is a form
                    // (inputs, buttons), so those children already provide
                    // scroll access and an extra stop on the container itself
                    // is pure noise: Tab into the panel would land on a
                    // non-interactive box before reaching the first field.
                    tabIndex={-1}
                    className="flex-1 overflow-y-auto px-6 py-5 focus:outline-none"
                  >
                    <SheetPortalContext.Provider value={panel}>
                      {children}
                    </SheetPortalContext.Provider>
                  </div>
                )}
                {footer && (
                  <div className="flex items-center justify-end gap-3 border-t border-border/60 bg-surface-muted/50 px-6 py-4">
                    {footer}
                  </div>
                )}
              </m.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
